import {
  AppCard,
  Icon,
  PageContainer,
  RouteIconButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiEye, mdiTruck } from "@mdi/js";
import {
  Box,
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useListQuotationPurchaseOrders } from "../../../../__generated__/orval/endpoints/purchase-orders/purchase-orders";
import { EntitiesPurchaseOrderQuotationCollectionListResponse } from "../../../../__generated__/orval/models/entitiesPurchaseOrderQuotationCollectionListResponse.zod";
import type { ListQuotationPurchaseOrdersParams } from "../../../../__generated__/orval/models/listQuotationPurchaseOrdersParams.zod";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";
import { PurchaseOrdersFilters } from "./-internal_components/PurchaseOrdersFilters/index";

type ListQuotationPurchaseOrdersParamsWithAdditionalFilters =
  ListQuotationPurchaseOrdersParams & {
    item_count?: number;
    sap_created_at?: string;
  };

const searchSchema = z.object({
  has_invoices: z.enum(["true", "false"]).optional().catch(undefined),
  item_count: z.coerce.number().int().positive().optional().catch(undefined),
  page: z.coerce.number().int().min(1).default(1).catch(1),
  purchase_order_number: z.string().optional().catch(undefined),
  responsible: z.string().optional().catch(undefined),
  rowsPerPage: z.coerce.number().int().min(1).default(25).catch(25),
  sap_created_at: z.string().optional().catch(undefined),
  vendor: z.string().optional().catch(undefined),
});

const PurchaseOrders: React.FC = () => {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const listParams: ListQuotationPurchaseOrdersParamsWithAdditionalFilters = {
    has_invoices:
      search.has_invoices === undefined
        ? undefined
        : search.has_invoices === "true",
    item_count: search.item_count,
    items_per_page: search.rowsPerPage,
    page: search.page,
    purchase_order_number: search.purchase_order_number,
    responsible: search.responsible,
    sap_created_at: search.sap_created_at,
    vendor: search.vendor,
  };
  const {
    data: response,
    isError,
    isLoading,
  } = useListQuotationPurchaseOrders(listParams);
  const parsedResponse =
    response === undefined
      ? undefined
      : EntitiesPurchaseOrderQuotationCollectionListResponse.safeParse(
          response,
        );
  const purchaseOrders =
    parsedResponse?.success === true ? parsedResponse.data.results : undefined;
  const hasInvalidResponse = parsedResponse?.success === false;

  useEffect(() => {
    if (isError || hasInvalidResponse) {
      enqueueSnackbar(t("quotationPurchaseOrders.fetchError"), {
        variant: "error",
      });
    }
  }, [hasInvalidResponse, isError, t]);

  return (
    <PageContainer
      maxWidth={false}
      showBackButton={false}
      title={t("quotationPurchaseOrders.title")}
    >
      <AppCard>
        <PurchaseOrdersFilters loading={isLoading} />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress aria-label={t("common.loading")} />
          </Box>
        ) : isError || hasInvalidResponse ? (
          <Typography
            color="error"
            role="alert"
            sx={{ py: 4, textAlign: "center" }}
          >
            {t("quotationPurchaseOrders.fetchError")}
          </Typography>
        ) : purchaseOrders?.length ? (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table
                aria-label={t("quotationPurchaseOrders.title")}
                size="small"
                sx={{ minWidth: 960 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("quotationPurchaseOrders.purchaseOrderNumber")}
                    </TableCell>
                    <TableCell>{t("common.vendor")}</TableCell>
                    <TableCell>{t("common.responsible")}</TableCell>
                    <TableCell>
                      {t("quotationPurchaseOrders.itemCount")}
                    </TableCell>
                    <TableCell>{t("common.createdAt")}</TableCell>
                    <TableCell align="center">
                      {t("quotationPurchaseOrders.invoices")}
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {t("common.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseOrders.map((purchaseOrder) => (
                    <TableRow hover key={purchaseOrder.id}>
                      <TableCell>{purchaseOrder.po_number}</TableCell>
                      <TableCell>{purchaseOrder.vendor_name}</TableCell>
                      <TableCell>
                        {purchaseOrder.responsible_name &&
                        purchaseOrder.responsible_email
                          ? `${purchaseOrder.responsible_name} - ${purchaseOrder.responsible_email}`
                          : purchaseOrder.responsible_name ||
                            purchaseOrder.responsible_email ||
                            "-"}
                      </TableCell>
                      <TableCell>{purchaseOrder.item_count}</TableCell>
                      <TableCell>
                        {t(
                          "intlDate",
                          i18nextParseToDate(purchaseOrder.sap_created_at),
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            purchaseOrder.has_invoices
                              ? t("quotationPurchaseOrders.invoiceAvailable")
                              : t("quotationPurchaseOrders.invoiceUnavailable")
                          }
                        >
                          <Box
                            component="span"
                            sx={{
                              color: purchaseOrder.has_invoices
                                ? "success.main"
                                : "action.disabled",
                            }}
                          >
                            <Icon icon={mdiTruck} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip
                          title={t("quotationPurchaseOrders.moreDetails")}
                        >
                          <RouteIconButtonLink
                            aria-label={t(
                              "quotationPurchaseOrders.moreDetails",
                            )}
                            params={{
                              purchaseOrderId: String(purchaseOrder.id),
                            }}
                            to="/purchase_orders/materials/$purchaseOrderId"
                          >
                            <Icon icon={mdiEye} />
                          </RouteIconButtonLink>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={Math.max(1, response?.pagination.total_pages ?? 1)}
                onChange={(_event, page) => {
                  navigate({ search: (previous) => ({ ...previous, page }) });
                }}
                page={search.page}
              />
            </Box>
          </>
        ) : (
          <Typography
            color="text.secondary"
            sx={{ py: 4, textAlign: "center" }}
          >
            {t("quotationPurchaseOrders.emptyState")}
          </Typography>
        )}
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/purchase_orders/materials/",
  // biome-ignore assist/source/useSortedKeys: ordering required by type inference, waiting a TanStack Biome plugin
)({
  validateSearch: searchSchema,
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Purchase Order - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: PurchaseOrders,
});
